// Tamil-Katral-Maiyam\src\components\marketing\StepTimeline.tsx

import React from "react";
import { LucideIcon } from "lucide-react";
import { useInView } from "../../hooks/useInView";
import { clsx } from "clsx";

export interface StepItem {
  step: number;
  title: string;
  description: string;
  icon: LucideIcon;
}

interface StepTimelineProps {
  steps: StepItem[];
  className?: string;
  variant?: "default" | "orange";
}

export const StepTimeline: React.FC<StepTimelineProps> = ({
  steps,
  className,
  variant = "orange",
}) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const isOrange = variant === "orange";

  return (
    <div ref={ref} className={clsx("relative", className)}>
      {/* Desktop: horizontal timeline */}
      <div className="hidden lg:grid lg:grid-cols-4 lg:gap-6 lg:items-stretch">
        {steps.map((s, i) => (
          <div
            key={s.step}
            className={clsx(
              "relative h-full transition-all duration-700 ease-out",
              inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            )}
            style={{ transitionDelay: inView ? `${i * 100}ms` : "0ms" }}
          >
            {/* connector line */}
            {i < steps.length - 1 && (
              <div
                className={clsx(
                  "absolute top-7 left-[calc(50%+32px)] w-[calc(100%-64px)] h-[3px] -translate-y-1/2 rounded-full",
                  isOrange ? "bg-orange-200 dark:bg-gray-800" : "bg-white/22",
                  inView ? "opacity-100" : "opacity-0"
                )}
                style={{
                  transitionDelay: inView ? `${(i + 1) * 100}ms` : "0ms",
                }}
                aria-hidden
              />
            )}

            <div className="flex h-full flex-col items-center text-center">
              {/* icon circle */}
              <div
                className={clsx(
                  "flex h-14 w-14 items-center justify-center rounded-2xl shrink-0 transition-transform duration-300 hover:scale-105",
                  isOrange
                    ? "bg-orange-500 text-white shadow-md shadow-orange-500/30"
                    : "border border-white/18 bg-white/12 backdrop-blur-md shadow-[0_18px_40px_rgba(0,0,0,0.35)] text-white"
                )}
              >
                <s.icon className="h-7 w-7" aria-hidden />
              </div>

              {/* step label */}
              <span
                className={clsx(
                  "mt-3.5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wider",
                  isOrange
                    ? "bg-orange-100 dark:bg-orange-950/80 text-orange-700 dark:text-orange-300 border border-orange-200/80 dark:border-orange-900/50"
                    : "border border-white/18 bg-black/25 text-white/95 drop-shadow-[0_3px_12px_rgba(0,0,0,0.70)]"
                )}
              >
                LANGKAH {s.step}
              </span>

              {/* Card */}
              <div
                className={clsx(
                  "mt-3.5 w-full rounded-2xl px-5 py-5 transition-all duration-300 hover:-translate-y-1",
                  isOrange
                    ? "border border-orange-200/80 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-md shadow-orange-500/5 hover:shadow-lg hover:shadow-orange-500/10"
                    : "border border-white/16 bg-white/10 backdrop-blur-md shadow-[0_18px_40px_rgba(0,0,0,0.35)]",
                  "h-full min-h-[170px] flex flex-col justify-center"
                )}
              >
                <h3
                  className={clsx(
                    "text-base font-extrabold leading-snug",
                    isOrange ? "text-gray-900 dark:text-white" : "text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.75)]"
                  )}
                >
                  {s.title}
                </h3>

                <p
                  className={clsx(
                    "mt-2 text-xs sm:text-sm font-medium leading-relaxed",
                    isOrange ? "text-gray-600 dark:text-gray-300" : "text-white/90 drop-shadow-[0_3px_12px_rgba(0,0,0,0.65)]"
                  )}
                >
                  {s.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile: vertical timeline */}
      <div className="lg:hidden space-y-5">
        {steps.map((s, i) => (
          <div
            key={s.step}
            className={clsx(
              "relative flex gap-4 transition-all duration-700 ease-out",
              inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
            )}
            style={{ transitionDelay: inView ? `${i * 100}ms` : "0ms" }}
          >
            {/* vertical connector */}
            {i < steps.length - 1 && (
              <div
                className={clsx(
                  "absolute left-[24px] top-[56px] bottom-0 w-[2px] -mb-5",
                  isOrange ? "bg-orange-200 dark:bg-gray-800" : "bg-white/22"
                )}
                aria-hidden
              />
            )}

            {/* icon */}
            <div
              className={clsx(
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl",
                isOrange
                  ? "bg-orange-500 text-white shadow-md shadow-orange-500/30"
                  : "border border-white/18 bg-white/12 backdrop-blur-md shadow-[0_18px_40px_rgba(0,0,0,0.35)] text-white"
              )}
            >
              <s.icon className="h-6 w-6" aria-hidden />
            </div>

            {/* card */}
            <div className="flex-1 pb-4">
              <span
                className={clsx(
                  "inline-flex rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wider",
                  isOrange
                    ? "bg-orange-100 dark:bg-orange-950/80 text-orange-700 dark:text-orange-300 border border-orange-200/80 dark:border-orange-900/50"
                    : "border border-white/18 bg-black/25 text-white/95 drop-shadow-[0_3px_12px_rgba(0,0,0,0.70)]"
                )}
              >
                LANGKAH {s.step}
              </span>

              <div
                className={clsx(
                  "mt-2.5 rounded-2xl px-5 py-4",
                  isOrange
                    ? "border border-orange-200/80 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-md shadow-orange-500/5"
                    : "border border-white/16 bg-white/10 backdrop-blur-md shadow-[0_18px_40px_rgba(0,0,0,0.35)]"
                )}
              >
                <h3
                  className={clsx(
                    "text-base font-extrabold",
                    isOrange ? "text-gray-900 dark:text-white" : "text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.75)]"
                  )}
                >
                  {s.title}
                </h3>
                <p
                  className={clsx(
                    "mt-1.5 text-xs sm:text-sm font-medium leading-relaxed",
                    isOrange ? "text-gray-600 dark:text-gray-300" : "text-white/90 drop-shadow-[0_3px_12px_rgba(0,0,0,0.65)]"
                  )}
                >
                  {s.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
